import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import StudentChoice from './StudentChoice';
import ToetsTemplates from './ToetsTemplates';
import { api, currentSchoolYear, displayDate, displayNumber, errorMessage, isToetsAdmin, schoolYears } from './toetsUtils';
import './Toetsen.css';
import './ToetsSelection.css';

export default function AllToetsen() {
  const { userInfo } = useAuth();
  const [query] = useSearchParams();
  const location = useLocation();
  const [filters, setFilters] = useState(() => ({ school_year: query.get('school_year') || currentSchoolYear(), student_id: query.get('student_id') || '', location_id: '', group_id: '', template_id: '', date_from: '', date_to: '' }));
  const [studentName, setStudentName] = useState(query.get('student_name') || '');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [debouncedStudentSearch, setDebouncedStudentSearch] = useState('');
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState({ average_grade: null, graded_count: 0 });
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [options, setOptions] = useState({ locations: [], groups: [], school_years: [] });
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [optionsError, setOptionsError] = useState('');
  const [message, setMessage] = useState(location.state?.message || '');
  const [refresh, setRefresh] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const deletingRef = useRef(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedRecords = results.filter(r => selectedIds.includes(r.id));
  const allSelected = results.length > 0 && selectedRecords.length === results.length;
  const someSelected = selectedRecords.length > 0 && !allSelected;
  const selectAll = checked => setSelectedIds(checked ? results.map(r => r.id) : []);
  const setMixedCheckbox = element => { if (element) element.indeterminate = someSelected; };
  useEffect(() => { setSelectedIds([]); }, [search, studentSearch]);
  const admin = isToetsAdmin(userInfo?.role);
  const isSingleUser = Boolean(userInfo?.is_single_user);
  useEffect(() => {
    const timer = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedStudentSearch(studentSearch.trim()); setPage(1); }, 350);
    return () => clearTimeout(timer);
  }, [studentSearch]);
  useEffect(() => {
    const controller = new AbortController();
    setOptionsError(''); setOptions({ locations: [], groups: [], school_years: [] });
    Promise.all([api('get', '/options', undefined, { signal: controller.signal }), api('get', '/templates', undefined, { signal: controller.signal })])
      .then(([meta, standard]) => { setOptions(meta.data.data); setTemplates(standard.data.data); })
      .catch(err => { if (!axios.isCancel(err)) setOptionsError(errorMessage(err)); });
    return () => controller.abort();
  }, [refresh, userInfo?.user_id, userInfo?.customer_id, userInfo?.role]);
  const refreshTemplates = useCallback(async () => {
    const response = await api('get', '/templates'); setTemplates(response.data.data);
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setSelectedIds([]); setSummary({ average_grade: null, graded_count: 0 });
    const params = { page, limit, search: debounced, student_search: debouncedStudentSearch };
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'location_id' && isSingleUser) return;
      if (value && value !== 'all') params[key] = value;
      if (key === 'school_year' && value === 'none') params[key] = '';
    });
    api('get', '', undefined, { params, signal: controller.signal })
      .then(response => { setResults(response.data.data); setPagination(response.data.pagination); setSummary(response.data.summary || { average_grade: null, graded_count: 0 }); })
      .catch(err => { if (!axios.isCancel(err)) { setError(errorMessage(err)); setResults([]); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, debounced, debouncedStudentSearch, page, limit, refresh, isSingleUser]);
  const changeFilter = (key, value) => { setSelectedIds([]); setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };
  const remove = async result => {
    if (deletingRef.current || !window.confirm(`Toets “${result.title}” van ${result.student_name} verwijderen?`)) return;
    deletingRef.current = true; setDeleting(true); setError(''); setMessage('');
    try {
      await api('delete', `/${result.id}`, { version: result.version });
      setMessage('Toets verwijderd.'); setRefresh(v => v + 1);
    } catch (err) { setError(errorMessage(err)); }
    finally { deletingRef.current = false; setDeleting(false); }
  };
  const removeSelected = async () => {
    if (deletingRef.current || loading || !selectedRecords.length) return;
    if (!window.confirm(`${selectedRecords.length} geselecteerde toetsresultaten definitief verwijderen, inclusief alle onderdelen? Dit kan niet ongedaan worden gemaakt.`)) return;
    deletingRef.current = true; setDeleting(true); setError(''); setMessage('');
    try {
      const response = await api('delete', '/bulk', { items: selectedRecords.map(r => ({ id: r.id, version: r.version })) });
      setSelectedIds([]); setMessage(response.data.message); setRefresh(v => v + 1);
    } catch (err) { setError(errorMessage(err)); }
    finally { deletingRef.current = false; setDeleting(false); }
  };
  const reset = () => {
    setSelectedIds([]);
    setFilters({ school_year: currentSchoolYear(), student_id: '', location_id: '', group_id: '', template_id: '', date_from: '', date_to: '' });
    setStudentName(''); setSearch(''); setStudentSearch(''); setPage(1);
  };
  const newLink = filters.student_id ? `/toetsen/nieuw?student_id=${filters.student_id}&student_name=${encodeURIComponent(studentName)}` : '/toetsen/nieuw';
  const currentPage = pagination.page || 1;
  const pages = Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => Math.max(1, Math.min(currentPage - 2, pagination.totalPages - 4)) + i);
  return <div className="toetsen-page">
    <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
      <div><h2>Toetsen</h2><p className="text-muted mb-0">Toetsresultaten en onderdelen per leerling.</p></div>
      <div className="d-flex flex-wrap gap-2">
        {admin && <button type="button" className="btn btn-outline-primary" aria-expanded={showTemplates} onClick={() => setShowTemplates(v => !v)}>Standaardtoetsen beheren</button>}
        <Link className="btn btn-primary" to={newLink}>+ Toetsresultaat</Link>
      </div>
    </div>
    {message && <div className="alert alert-success" role="status">{message}</div>}
    {optionsError && <div className="alert alert-warning" role="alert">{optionsError} <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRefresh(v => v + 1)}>Opnieuw laden</button></div>}
    {admin && showTemplates && <ToetsTemplates templates={templates} onRefresh={refreshTemplates} />}
    <section className="toets-filters" aria-label="Toetsresultaten filteren">
      <div className="row g-3">
        <div className="col-md-6"><label className="form-label" htmlFor="toets-search">Zoeken</label>
          <input id="toets-search" className="form-control" type="search" placeholder="Titel of leerlingnaam…" value={search} maxLength={200} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="col-md-3"><label className="form-label" htmlFor="filter-schoolyear">Schooljaar</label>
          <select id="filter-schoolyear" className="form-select" value={filters.school_year} onChange={e => changeFilter('school_year', e.target.value)}>
            <option value="all">Alle schooljaren</option><option value="none">Zonder schooljaar</option>
            {schoolYears(options.school_years).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {!isSingleUser && <div className="col-md-3"><label className="form-label" htmlFor="filter-location">Locatie</label>
          <select id="filter-location" className="form-select" value={filters.location_id} onChange={e => changeFilter('location_id', e.target.value)}>
            <option value="">Alle locaties</option><option value="none">Zonder locatie</option>
            {options.locations.map(l => <option key={l.location_id} value={l.location_id}>{l.location_name}</option>)}
          </select>
        </div>}
        <div className="col-md-6"><label className="form-label" htmlFor="filter-student">Leerling</label>
          <StudentChoice allLabel={userInfo?.role === 'docent' ? 'Mijn leerlingen' : 'Alle leerlingen'} id="filter-student" value={filters.student_id} selectedName={studentName}
            searchValue={studentSearch} onSearchChange={value => {
              setStudentSearch(value); setStudentName(''); changeFilter('student_id', '');
            }}
            onChange={(id, name) => { changeFilter('student_id', id); setStudentName(name); }} />
        </div>
        <div className="col-md-3"><label className="form-label" htmlFor="filter-group">Groep</label>
          <select id="filter-group" className="form-select" value={filters.group_id} onChange={e => changeFilter('group_id', e.target.value)}>
            <option value="">{userInfo?.role === 'docent' ? 'Mijn groepen' : 'Alle groepen'}</option>{options.groups.map(g => <option key={g.groep_id} value={g.groep_id}>{g.groep_name} ({g.school_year})</option>)}
          </select>
        </div>
        <div className="col-md-3"><label className="form-label" htmlFor="filter-template">Standaardtoets</label>
          <select id="filter-template" className="form-select" value={filters.template_id} onChange={e => changeFilter('template_id', e.target.value)}>
            <option value="">Alle toetsen</option>{templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </div>
        <div className="col-sm-4"><label className="form-label" htmlFor="date-from">Datum vanaf</label>
          <input id="date-from" type="date" className="form-control" value={filters.date_from} onChange={e => changeFilter('date_from', e.target.value)} />
        </div>
        <div className="col-sm-4"><label className="form-label" htmlFor="date-to">Datum tot en met</label>
          <input id="date-to" type="date" className="form-control" value={filters.date_to} onChange={e => changeFilter('date_to', e.target.value)} />
        </div>
        <div className="col-sm-4 d-flex align-items-end"><button type="button" className="btn btn-outline-secondary" onClick={reset}>Filters herstellen</button></div>
      </div>
    </section>
    {error && <div className="alert alert-danger" role="alert">{error} <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setRefresh(v => v + 1)}>Opnieuw laden</button></div>}
    <div className="toets-panel">
      <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
        <div aria-live="polite">{loading ? 'Toetsen laden…' : `${pagination.total} toetsresultaten`}</div>
        <label className="d-flex align-items-center gap-2 small">Per pagina
          <select className="form-select form-select-sm" aria-label="Resultaten per pagina" value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      {!loading && !error && <div className="toets-selection-summary mb-3" role="status">
        <strong>Gemiddelde gefilterde resultaten: {displayNumber(summary.average_grade)}</strong>
        <span className="small text-muted">Gebaseerd op {summary.graded_count} resultaten met een cijfer, over alle pagina’s.</span>
      </div>}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
        <label className="d-flex align-items-center gap-2 mb-0">
          <input type="checkbox" className="form-check-input mt-0" ref={setMixedCheckbox}
            checked={allSelected} disabled={loading || deleting || !results.length}
            onChange={e => selectAll(e.target.checked)} />
          Alles op deze pagina selecteren
        </label>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <span className="small" aria-live="polite">{selectedRecords.length} geselecteerd</span>
          <button type="button" className="btn btn-danger btn-sm" disabled={loading || deleting || !selectedRecords.length}
            onClick={removeSelected}>{deleting ? 'Verwijderen…' : `Geselecteerde verwijderen (${selectedRecords.length})`}</button>
        </div>
      </div>
      {!loading && !error && <>
        <div className="table-responsive"><table className="table toetsen-table">
          <thead><tr><th scope="col"><input type="checkbox" className="form-check-input" ref={setMixedCheckbox}
            aria-label="Alle toetsresultaten op deze pagina selecteren" checked={allSelected}
            disabled={deleting || !results.length} onChange={e => selectAll(e.target.checked)} /></th><th>Leerling</th><th>Titel</th><th>Schooljaar</th><th>Datum</th><th>Goed</th><th>Fout</th><th>Cijfer</th><th>Acties</th></tr></thead>
          <tbody>{results.map(r => <tr key={r.id} className={r.grade !== null && r.grade !== undefined && r.grade !== '' && Number.isFinite(Number(r.grade)) && Number(r.grade) < 5.5 ? 'toets-result-insufficient' : undefined}>
            <td><input type="checkbox" className="form-check-input" aria-label={`Selecteer ${r.title} van ${r.student_name}`}
              checked={selectedIds.includes(r.id)} disabled={deleting}
              onChange={e => setSelectedIds(ids => e.target.checked ? [...ids, r.id] : ids.filter(id => id !== r.id))} /></td>
            <td>{r.student_name}</td><td className="toets-title"><Link to={`/toetsen/${r.id}`}>{r.title}</Link>
              {r.part_count > 0 && <div className="small text-muted">{r.part_count} onderdelen</div>}</td>
            <td>{r.school_year || '—'}</td><td className="text-nowrap">{displayDate(r.test_date)}</td>
            <td>{displayNumber(r.correct_count)}</td><td>{displayNumber(r.incorrect_count)}</td><td>{displayNumber(r.grade)}</td>
            <td><div className="d-flex gap-2"><Link className="btn btn-sm btn-outline-primary" to={`/toetsen/${r.id}/bewerken`}>Bewerken</Link>
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={deleting} onClick={() => remove(r)}>Verwijderen</button></div></td>
          </tr>)}</tbody>
        </table></div>
        {!results.length && <p className="text-muted text-center py-4">Geen toetsen gevonden met deze filters.</p>}
        <nav className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3" aria-label="Paginering toetsen">
          <small>Pagina {currentPage} van {pagination.totalPages}</small>
          <div className="d-flex flex-wrap gap-2"><button type="button" className="btn btn-sm btn-outline-primary" disabled={!pagination.hasPrevPage} onClick={() => setPage(currentPage - 1)}>Vorige</button>
            {pages.map(n => <button type="button" className={`btn btn-sm ${n === currentPage ? 'btn-primary' : 'btn-outline-primary'}`} key={n}
              aria-current={n === currentPage ? 'page' : undefined} onClick={() => setPage(n)}>{n}</button>)}
            <button type="button" className="btn btn-sm btn-outline-primary" disabled={!pagination.hasNextPage} onClick={() => setPage(currentPage + 1)}>Volgende</button>
          </div>
        </nav>
      </>}
    </div>
  </div>;
}
